/**
 * Fetch one payout statement as a PDF and hand it to the browser (COL-528).
 *
 * `supabase.functions.invoke` is not used: it parses the response, and this
 * endpoint returns PDF bytes rather than JSON. A direct fetch keeps the blob
 * intact and lets a 404 be told apart from a transport failure.
 *
 * The function is given only an invoice id and the caller's session. Which
 * statements that session may have is decided by `owner_payout_statements()`,
 * server-side — so there is nothing to check here beyond "did it come back".
 */
import { useMutation } from "@tanstack/react-query";
import { supabase, supabaseUrl } from "@/lib/supabase";

const FUNCTION_NAME = "owner-payout-statement";

export function useDownloadPayoutStatement() {
  return useMutation({
    mutationFn: async (invoiceId: string): Promise<void> => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Not signed in");
      }

      // supabaseUrl, not the raw env var: the client also supports being
      // configured with VITE_SUPABASE_PROJECT_ID alone, where VITE_SUPABASE_URL
      // is undefined and this would POST to "undefined/functions/v1/...".
      const res = await fetch(`${supabaseUrl}/functions/v1/${FUNCTION_NAME}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ invoiceId }),
      });
      if (!res.ok) {
        throw new Error(`Statement request failed (${res.status})`);
      }

      const blob = await res.blob();
      const name =
        filenameFromDisposition(res.headers.get("Content-Disposition")) ??
        `Auszahlung-${invoiceId}.pdf`;
      saveBlob(blob, name);
    },
  });
}

/** `attachment; filename="Auszahlung-GS-202608-001.pdf"` → the filename. */
export function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1] ?? null;
}

/**
 * The object URL is revoked on the next tick, not immediately: Safari starts
 * the download asynchronously and a URL revoked in the same frame produces an
 * empty file.
 */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
